import React, { useCallback, useMemo, useState } from "react";
import {
  Link,
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  getProductById,
  getProductBySlug,
  matchesQuery,
  productCategories,
  productPath,
  productsData,
} from "../data/productsData";
import { company } from "../data/companyData";
import {
  breadcrumbSchema,
  catalogueSchema,
  pageMeta,
  productMeta,
  productSchema,
} from "../data/seoData";
import ProductCard from "../components/ProductCard";
import ProductModal from "../components/ProductModal";
import Reveal from "../components/Reveal";
import Seo from "../components/Seo";
import "./Products.css";

const ALL = "All";

const Products = () => {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState(ALL);
  const [query, setQuery] = useState("");

  /* Detail drawer is driven by the URL so every product is shareable and
     independently indexable. */
  const active = useMemo(() => (slug ? getProductBySlug(slug) : null), [slug]);

  /* Links shared before the slug routes existed used ?product=<id>. */
  const legacy = params.get("product");
  const legacyTarget = legacy && !slug ? getProductById(legacy) : null;

  const openProduct = useCallback(
    (product) => navigate(productPath(product)),
    [navigate]
  );

  const closeProduct = useCallback(
    () => navigate("/products", { replace: true }),
    [navigate]
  );

  const counts = useMemo(() => {
    const map = { [ALL]: productsData.length };
    for (const p of productsData) map[p.category] = (map[p.category] || 0) + 1;
    return map;
  }, []);

  const visible = useMemo(
    () =>
      productsData.filter(
        (p) =>
          (category === ALL || p.category === category) && matchesQuery(p, query)
      ),
    [category, query]
  );

  /* A slug nobody publishes should not answer 200 with the bare catalogue. */
  if (slug && !active) return <Navigate to="/products" replace />;
  if (legacyTarget) return <Navigate to={productPath(legacyTarget)} replace />;

  const seo = active
    ? {
        ...productMeta(active, productPath(active)),
        schemas: [
          ["product", productSchema(active, productPath(active))],
          [
            "breadcrumb",
            breadcrumbSchema([
              { name: "Products", path: "/products" },
              { name: active.name, path: productPath(active) },
            ]),
          ],
        ],
      }
    : {
        ...pageMeta.products,
        schemas: [
          ["catalogue", catalogueSchema(productsData, productPath)],
          ["breadcrumb", breadcrumbSchema([{ name: "Products", path: "/products" }])],
        ],
      };

  return (
    <>
      <Seo {...seo} />
      <header className="phero">
        <div className="phero__glow" />
        <div className="phero__grid" />
        <div className="shell phero__inner">
          <nav className="crumbs" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span aria-hidden="true">/</span>
            <span>Products</span>
          </nav>

          {/* One <h1> per page: on /products/:slug the drawer owns it. */}
          <Reveal
            as={active ? "p" : "h1"}
            className="phero__title"
            variant="mask">
            The catalogue
          </Reveal>
          <Reveal as="p" className="phero__lead" delay={0.1}>
            {productsData.length} industrial and technical grade compounds held
            in Mumbai. Purity, packing and typical applications are published
            for every line. Open any product for its full specification.
          </Reveal>
        </div>
      </header>

      <div className="pbar">
        <div className="shell pbar__inner">
          <div className="filters" role="group" aria-label="Filter by category">
            {[ALL, ...productCategories].map((cat) => (
              <button
                key={cat}
                type="button"
                className={`filter${category === cat ? " is-active" : ""}`}
                onClick={() => setCategory(cat)}
                aria-pressed={category === cat}>
                {cat}
                <span className="filter__count">{counts[cat]}</span>
              </button>
            ))}
          </div>

          <div className="pbar__search">
            <i className="fas fa-search" aria-hidden="true" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter by name, formula or use…"
              aria-label="Filter products"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label="Clear filter">
                ✕
              </button>
            )}
          </div>
        </div>
      </div>

      <section className="section on-light">
        <div className="shell">
          <p className="pcount mono">
            Showing {visible.length} of {productsData.length}
            {category !== ALL ? ` · ${category}` : ""}
          </p>

          {visible.length === 0 ? (
            <div className="empty">
              <div className="empty__icon">
                <i className="fas fa-flask" aria-hidden="true" />
              </div>
              <h3>No match in stock</h3>
              <p>
                We import to specification as well. Send us the compound, grade
                and quantity and we will come back with a landed price.
              </p>
              <Link to="/contact" className="btn btn--accent" style={{ marginTop: "1.5rem" }}>
                Request a compound
                <span className="btn__arrow" aria-hidden="true">→</span>
              </Link>
            </div>
          ) : (
            <div className="product-grid">
              {visible.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onOpen={openProduct}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="cta-band">
        <div className="cta-band__glow" />
        <div className="shell cta-band__inner">
          <div>
            <p className="eyebrow eyebrow--dark">Bulk enquiries</p>
            <h2 className="cta-band__title">
              Ask for a landed price, not a list price.
            </h2>
          </div>
          <div>
            <div className="cta-band__actions">
              <Link to="/contact" className="btn btn--accent btn--lg">
                Request a quote
                <span className="btn__arrow" aria-hidden="true">→</span>
              </Link>
              <a
                href={`tel:${company.phones.office.tel}`}
                className="btn btn--ghost-light btn--lg">
                {company.phones.office.display}
              </a>
            </div>
            <p className="cta-band__note">
              Quantity, grade and delivery location are all we need to quote.
            </p>
          </div>
        </div>
      </section>

      <ProductModal product={active} onClose={closeProduct} />
    </>
  );
};

export default Products;
